import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Copy, ChevronUp, ChevronDown, ListChecks, Search, Check, X } from "lucide-react";
import { confirmDialog } from "@/lib/dialogs";

function Recipients({ endpoint, title, description, testid, prefix }) {
  const [recips, setRecips] = useState([]);
  const [users, setUsers] = useState([]);
  const [pick, setPick] = useState("");
  const load = () => { api.get(endpoint).then((r) => setRecips(r.data)); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, [endpoint]);
  const save = async (list) => { setRecips(list); await api.put(endpoint, { recipients: list }); };
  const add = () => {
    const u = users.find((x) => x.id === pick);
    if (!u) return;
    if (recips.some((r) => r.user_id === u.id)) return toast.error("Already added");
    save([...recips, { user_id: u.id, name: u.name, email: u.email, enabled: true }]);
    setPick("");
  };
  return (
    <Card className="p-5 rounded-md" data-testid={testid}>
      <h3 className="font-alt font-semibold text-cass-navy mb-1">{title}</h3>
      <p className="text-xs text-cass-slate mb-3">{description}</p>
      <div className="space-y-2 mb-3">
        {recips.map((r, i) => (
          <div key={r.user_id} className="flex items-center justify-between border border-border rounded-md px-3 py-2" data-testid={`${prefix}-recipient-${r.user_id}`}>
            <div><div className="text-sm font-medium text-cass-navy">{r.name}</div><div className="text-xs text-cass-slate">{r.email}</div></div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-xs text-cass-slate"><Switch checked={r.enabled !== false} onCheckedChange={(v) => save(recips.map((x, j) => j === i ? { ...x, enabled: v } : x))} data-testid={`${prefix}-recipient-toggle-${r.user_id}`} />Enabled</label>
              <Button size="sm" variant="ghost" onClick={() => save(recips.filter((_, j) => j !== i))} data-testid={`${prefix}-recipient-remove-${r.user_id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {!recips.length && <p className="text-sm text-cass-slate">No recipients yet.</p>}
      </div>
      <div className="flex items-center gap-2">
        <Select value={pick} onValueChange={setPick}>
          <SelectTrigger className="w-72" data-testid={`${prefix}-recipient-user-select`}><SelectValue placeholder="Add a user…" /></SelectTrigger>
          <SelectContent>{users.filter((u) => !recips.some((r) => r.user_id === u.id)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={add} disabled={!pick} className="bg-cass-navy text-white" data-testid={`${prefix}-recipient-add-btn`}><Plus className="w-4 h-4 mr-1" />Add</Button>
      </div>
    </Card>
  );
}

const ENGAGER_ROLES = ["super_admin", "pricing_admin", "engager"];

function TaskEditor({ service, onClose, onChanged }) {
  const sid = service.service_id;
  const [tasks, setTasks] = useState([]);
  const [edit, setEdit] = useState(null);
  const [starter, setStarter] = useState(false);
  const [users, setUsers] = useState([]);
  const load = () => api.get(`/engager/services/${sid}/tasks`).then((r) => { setTasks(r.data.tasks); setStarter(r.data.has_starter); });
  useEffect(() => { load(); }, [sid]);
  useEffect(() => { api.get("/users").then((r) => setUsers((r.data || []).filter((u) => ENGAGER_ROLES.includes(u.role)))).catch(() => {}); }, []);
  const refresh = async () => { await load(); onChanged?.(); };
  const addTask = async () => { await api.post(`/engager/services/${sid}/tasks`, { title: "New task" }); refresh(); };
  const seed = async () => { try { const r = await api.post(`/engager/services/${sid}/seed-tasks`); toast.success(`Added ${r.data.added} suggested tasks`); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Could not add suggested tasks"); } };
  const saveTask = async () => { await api.put(`/engager/tasks/${edit.id}`, { title: edit.title, description: edit.description }); setEdit(null); refresh(); };
  const del = async (t) => { if (!(await confirmDialog({ title: "Delete task?", description: `Delete “${t.title}”?`, tone: "destructive", confirmText: "Delete" }))) return; await api.delete(`/engager/tasks/${t.id}`); refresh(); };
  const dup = async (t) => { await api.post(`/engager/tasks/${t.id}/duplicate`); refresh(); };
  const toggle = async (t, v) => { await api.put(`/engager/tasks/${t.id}`, { enabled: v }); refresh(); };
  const move = async (i, dir) => { const arr = [...tasks]; const to = i + dir; if (to < 0 || to >= arr.length) return; const [m] = arr.splice(i, 1); arr.splice(to, 0, m); setTasks(arr); await api.post(`/engager/services/${sid}/tasks/reorder`, { order: arr.map((x) => x.id) }); onChanged?.(); };
  const assign = async (t, uid) => { const u = users.find((x) => x.id === uid); await api.put(`/engager/tasks/${t.id}`, { assigned_user_id: u ? u.id : null, assigned_user_name: u ? u.name : null }); refresh(); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="task-editor">
        <DialogHeader><DialogTitle>Engager tasks — {service.name}{service.archived && <Badge variant="outline" className="ml-2">archived</Badge>}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {tasks.map((t, i) => (
            <div key={t.id} className={`border border-border rounded-md p-3 ${t.enabled === false ? "opacity-50" : ""}`} data-testid={`task-${t.id}`}>
              {edit?.id === t.id ? (
                <div className="space-y-2">
                  <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} placeholder="Task title" data-testid="task-edit-title" />
                  <Textarea value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder="Optional description / guidance" className="text-sm" />
                  <div className="flex gap-2"><Button size="sm" className="bg-cass-navy text-white" onClick={saveTask} data-testid="task-save-btn"><Check className="w-4 h-4 mr-1" />Save</Button><Button size="sm" variant="ghost" onClick={() => setEdit(null)}><X className="w-4 h-4 mr-1" />Cancel</Button></div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <div className="flex flex-col">
                      <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`task-up-${t.id}`}><ChevronUp className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === tasks.length - 1} onClick={() => move(i, 1)} data-testid={`task-down-${t.id}`}><ChevronDown className="w-4 h-4" /></Button>
                    </div>
                    <div className="min-w-0"><div className="text-sm font-medium text-cass-navy">{t.title}</div>{t.description && <div className="text-xs text-cass-slate">{t.description}</div>}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Select value={t.assigned_user_id || "unassigned"} onValueChange={(v) => assign(t, v === "unassigned" ? null : v)}>
                      <SelectTrigger className="h-7 w-40 text-xs" data-testid={`task-assignee-${t.id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1 text-xs text-cass-slate"><Switch checked={t.enabled !== false} onCheckedChange={(v) => toggle(t, v)} data-testid={`task-toggle-${t.id}`} />On</label>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit({ id: t.id, title: t.title, description: t.description })} data-testid={`task-edit-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => dup(t)} data-testid={`task-dup-${t.id}`}><Copy className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del(t)} data-testid={`task-del-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {!tasks.length && <p className="text-sm text-cass-slate">No Engager tasks configured for this service yet.</p>}
        </div>
        {!tasks.length && starter && <Button onClick={seed} variant="outline" className="w-full mt-2 border-cass-teal text-cass-teal" data-testid="seed-tasks-btn"><Plus className="w-4 h-4 mr-1" />Load suggested tasks</Button>}
        <Button onClick={addTask} variant="outline" className="w-full mt-2" data-testid="add-task-btn"><Plus className="w-4 h-4 mr-1" />Add task</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function EngagerConfig() {
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const load = () => api.get("/engager/services").then((r) => setServices(r.data));
  useEffect(() => { load(); }, []);
  const seedAll = async () => {
    if (!(await confirmDialog({ title: "Load suggested tasks for all unconfigured services?", description: "This loads the suggested checklist into every visible service that has no tasks yet. Services that already have tasks are skipped and nothing is overwritten.", confirmText: "Load suggested tasks" }))) return;
    try { const r = await api.post("/engager/services/seed-unconfigured"); toast.success(`Added ${r.data.added} tasks across ${r.data.services} service${r.data.services === 1 ? "" : "s"}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load suggested tasks"); }
  };
  const archivedCount = services.filter((s) => s.archived).length;
  const visible = showArchived ? services : services.filter((s) => !s.archived);
  const shown = q.trim() ? visible.filter((s) => `${s.name} ${s.category}`.toLowerCase().includes(q.trim().toLowerCase())) : visible;
  const byCat = shown.reduce((a, s) => { (a[s.category] ||= []).push(s); return a; }, {});
  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6" data-testid="engager-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-cass-navy flex items-center gap-2"><ListChecks className="w-7 h-7 text-cass-teal" />Engager Task Configuration</h1>
          <p className="text-cass-slate mt-1">Standard Engager setup tasks for each Service Catalogue item. Pricing stays in the Service Catalogue — no pricing here.</p>
        </div>
        <Button variant="outline" onClick={() => (window.location.href = "/engager/board")} data-testid="to-board-btn"><ListChecks className="w-4 h-4 mr-1" />Onboarding Board</Button>
      </div>
      <Recipients endpoint="/engager/recipients" testid="engager-recipients" prefix="engager"
        title="Engager Setup notification recipients"
        description="These users are emailed when a proposal is accepted and Engager setup is needed." />
      <Recipients endpoint="/onboarding-notify/recipients" testid="onboarding-recipients" prefix="onboarding"
        title="Onboarding completed notification recipients"
        description="These users are emailed when a client completes and submits their onboarding form." />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-cass-slate absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services…" className="pl-9" data-testid="engager-search" />
        </div>
        <Button variant="outline" className="border-cass-teal text-cass-teal shrink-0" onClick={seedAll} data-testid="seed-all-btn"><Plus className="w-4 h-4 mr-1" />Load suggested tasks for all unconfigured services</Button>
      </div>
      <div className="flex items-center justify-end">
        <label className="flex items-center gap-2 text-xs text-cass-slate">
          <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived-toggle" />
          Show archived{archivedCount ? ` (${archivedCount})` : ""}
        </label>
      </div>
      {Object.keys(byCat).map((cat) => (
        <Card key={cat} className="p-5 rounded-md" data-testid={`engager-cat-${cat}`}>
          <h3 className="font-alt font-semibold text-cass-navy mb-3">{cat}</h3>
          <div className="space-y-2">
            {byCat[cat].map((s) => (
              <div key={s.service_id} className="flex items-center justify-between border border-border rounded-md px-3 py-2" data-testid={`engager-service-${s.service_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-cass-navy truncate">{s.name}</span>
                  {s.archived && <Badge variant="outline" className="text-cass-slate">archived</Badge>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-cass-slate">{s.task_count} task{s.task_count === 1 ? "" : "s"}</span>
                  <Badge variant={s.configured ? "secondary" : "outline"} className={s.configured ? "text-cass-teal border-cass-teal" : "text-cass-slate"}>{s.configured ? "Configured" : "Not Configured"}</Badge>
                  <Button size="sm" variant="outline" onClick={() => setOpen(s)} data-testid={`configure-${s.service_id}`}>Configure</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
      {open && <TaskEditor service={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
